<template>
  <div v-resize="onResize" syle="overflow-y: hidden">
    <div class="d-flex align-center justify-center my-2">
      <v-chip
        small
        v-if="interventionId"
        class="mr-8"
        close
        @click:close="onInterventionClose"
      >
        {{
          generateMessageFunction('properties.documents.interventionId', {
            interventionId,
          })
        }}
      </v-chip>
      <custom-btn-icon
        @click.stop="openDialog"
        btn-text="Ajouter un document"
        color="success"
        icon="mdi-plus"
        :disabled="!!error"
      />
    </div>
    <v-data-table
      fixed-header
      disable-pagination
      hide-default-footer
      multi-sort
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDESC"
      :server-items-length="items.length"
      :height="height"
      :headers="headers"
      :loading="loader"
      :items="items"
      :no-data-text="noDataText"
      @click:row="download"
      class="elevation-1 secondarybackground striped-table clickable"
    >
      <template v-slot:top>
        <property-header />
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          small
          icon
          color="error"
          :loading="item | isLoading(deleting)"
          @click.stop="deleteItem(item)"
          v-if="item.internal"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>
      <template v-slot:item.category_id="{ item }">
        {{ categories.find((el) => el.id === item.category_id).title }}
      </template>
      <template v-slot:item.nature_id="{ item }">
        {{
          categories
            .find((el) => el.id === item.category_id)
            .natures.find(
              (el) =>
                el.id === item.nature_id.slice(item.nature_id.indexOf('N'))
            ).title
        }}
      </template>
      <template v-slot:item.last_update_datetime="{ item }">
        {{ new Date(item.last_update_datetime).toLocaleString() }}
      </template>
      <template v-slot:item.file_length="{ item }">
        {{ item.file_length | formatBytes }}
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { createNamespacedHelpers, mapActions } from 'vuex';
import resizable from '@/business/mixins/resizable';
import { deleteDocument } from '@/services/services/documentsService';
import { deleteDocumentInAction } from '@/services/services/actions.service';
import { ACTIONS_NATURE_ID } from '@/store/eMediaModule';
import { catchCRUDError } from '@/business/apiTreatment/response/errorTreatment';
import formatBytes from '@/business/utils/files';
import CustomBtnIcon from '@/components/displays/CustomBtnIcon';
import sortable from '@/business/mixins/sortable';
import { generateMessageFunction } from '@/utils/i18n';
import PropertyHeader from './PropertyHeader';
import downloadDocument from './downloadDocument';

const { mapGetters } = createNamespacedHelpers('documents');
export default {
  name: 'PropertyDocuments',
  components: { PropertyHeader, CustomBtnIcon },
  mixins: [resizable, sortable, downloadDocument],
  props: {
    isModal: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  methods: {
    deleteDocument,
    generateMessageFunction,
    deleteDocumentInAction,
    ...mapActions({
      showModal: 'modal/showModal',
      loadItems: 'documents/loadItems',
      setSnackbarOpen: 'snackbar/setSnackbarOpen',
    }),
    resetInterventionId() {
      this.$store.commit('documents/SET_INTERVENTION_ID', null);
    },
    onInterventionClose() {
      this.resetInterventionId();
      this.loadItems();
    },
    openDialog() {
      this.showModal({
        componentName: 'UploadDocumentDialog',
        componentProps: { action: !!this.actionId },
        dialogProps: { width: '70vw', 'max-width': '850px' },
      });
    },
    deleteItem(item) {
      this.deleting = item;
      this.deleteDocument(item.id, this.groupId)
        .then(() => {
          if (
            item.nature_id === ACTIONS_NATURE_ID &&
            !!item.technical_element
          ) {
            this.deleteDocumentInAction(item.technical_element, item.id);
          }
          this.loadItems();
        })
        .catch((err) => {
          this.setSnackbarOpen(catchCRUDError('DELETE', err));
        })
        .finally(() => {
          this.deleting = null;
        });
    },
  },
  computed: {
    ...mapGetters(['items', 'isLoading', 'categories', 'error', 'actionId']),
    loader() {
      return this.isLoading || !!this.downloading;
    },
    groupId() {
      return this.$store.getters['global/groupId'];
    },
    interventionId() {
      return this.$store.getters['documents/interventionId'];
    },
    noDataText() {
      if (
        this.error &&
        this.error.length &&
        this.error.find((err) => err.code === 'Installation not found')
      ) {
        return `Impossible de récupérer les documents sur cette installation, merci de contacter le support Emedia.`;
      }
      if (this.error) {
        return 'Une erreur technique est survenue.';
      }
      return 'Aucun document ne correspond à cette recherche.';
    },
    height() {
      if (this.isModal) {
        return null;
      }
      if (this.actionId) {
        return Math.max(400, this.contentHeightTable - 360);
      }
      return this.contentHeightTable - 60;
    },
  },
  watch: {
    error(error) {
      if (error) {
        this.$store.dispatch('snackbar/setSnackbarOpen', {
          message:
            'Une erreur est survenue lors de la récupération des documents.',
          color: 'error',
          more: error.length
            ? error.map((err) => ({ error: err.message }))
            : error.map((err) => ({ error: err })),
          closeable: true,
        });
      }
    },
  },
  filters: {
    formatBytes,
    isLoading(item, deleting) {
      return deleting && item.id === deleting.id;
    },
  },
  data: () => ({
    deleting: null,
    namespace: 'documents',
    headers: [
      { text: 'Titre', value: 'title' },
      { text: 'Nom', value: 'filename', sortable: false },
      { text: 'Catégorie', value: 'category_id', sortable: false },
      { text: 'Nature', value: 'nature_id', sortable: false },
      { text: 'Taille', value: 'file_length', width: '80px', sortable: false },
      { text: 'Dernière Modification', value: 'last_update_datetime' },
      { text: '', value: 'actions', sortable: false },
    ].map((item) => {
      return { ...item, class: 'secondarybackground' };
    }),
  }),
  beforeRouteLeave(to, from, next) {
    this.resetInterventionId();
    next();
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/table.scss';
</style>
